import { MicVAD, getDefaultRealTimeVADOptions, type RealTimeVADOptions } from '@ricky0123/vad-web';
import type { SpeechProbabilities } from '@ricky0123/vad-web/dist/models';
import { ACOUSTIC_DEFAULTS } from '../config';

type VadCaptureCallbacks = {
  onFrameProcessed: (probabilities: SpeechProbabilities, frame: Float32Array) => void;
  onSpeechStart?: () => void;
  onSpeechEnd?: (audio: Float32Array) => void;
};

type VadCaptureOptions = {
  callbacks: VadCaptureCallbacks;
};

const VAD_ASSET_CDN = 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.30/dist/';

export class VadCapture {
  private readonly callbacks: VadCaptureCallbacks;

  private vad: MicVAD | null = null;

  constructor(options: VadCaptureOptions) {
    this.callbacks = options.callbacks;
  }

  async start(): Promise<void> {
    if (this.vad) return;

    const vadOptions: Partial<RealTimeVADOptions> = {
      ...getDefaultRealTimeVADOptions('v5'),
      positiveSpeechThreshold: ACOUSTIC_DEFAULTS.vadPositiveThreshold,
      negativeSpeechThreshold: ACOUSTIC_DEFAULTS.vadNegativeThreshold,
      redemptionMs: ACOUSTIC_DEFAULTS.vadRedemptionMs,
      preSpeechPadMs: ACOUSTIC_DEFAULTS.vadPreSpeechPadMs,
      minSpeechMs: ACOUSTIC_DEFAULTS.vadMinSpeechMs,
      submitUserSpeechOnPause: false,
      model: 'v5',
      baseAssetPath: VAD_ASSET_CDN,
      onnxWASMBasePath: '/',
      processorType: 'AudioWorklet',
      startOnLoad: false,
      getStream: () =>
        navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        }),
      onFrameProcessed: (probabilities, frame) => {
        this.callbacks.onFrameProcessed(probabilities, frame);
      },
      onSpeechStart: () => {
        this.callbacks.onSpeechStart?.();
      },
      onSpeechEnd: (audio) => {
        this.callbacks.onSpeechEnd?.(audio);
      },
      onVADMisfire: () => {},
      onSpeechRealStart: () => {}
    };

    this.vad = await MicVAD.new(vadOptions);
    await this.vad.start();
  }

  async stop(): Promise<void> {
    if (!this.vad) return;
    await this.vad.pause();
    await this.vad.destroy();
    this.vad = null;
  }
}
